import * as yup from 'yup'
import { cloneDeep, remove, set } from 'lodash'
import { computed, markRaw } from 'vue'

import {
  Transaction,
  TransactionComputedEntry,
  TransactionEntry,
  TransactionSettings,
  TransactionSettingsAsset,
} from './utils/types'
import { TransactionAmountRule } from './utils/enums'
import { Entity } from '../utils/common'
import { EntityEvent } from '../utils/enums'
import { EntityErrors } from '../utils/types'

import { TRANSACTION_DEFAULT_DATA, TRANSACTION_FIELD } from './utils/const'

import {
  Rule,
  makeRequired,
  rules,
  dependencyRules,
  NEGATIVE_RULES,
  POSITIVE_RULES,
} from '@/helpers/validate'
import { numberParse } from '@/helpers/numbers'
import { displayAsset, displayAmount } from './utils/helpers'
import { parseDate, stringToLocalDateString } from '@/helpers/dates'
import { preSetAmount, preSetAsset } from './components/utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'
import { useModalsStore } from '@/store/modals'
import { useRepositoriesStore } from '@/store/repositories/index'
import { useTransactionsBunchStore } from '@/store/transactions/bunch'
import { useTransactionsSettingsStore } from '@/store/transactions/settings'
import { useTransactionsStore } from '@/store/transactions'

import TransactionDrawer from './components/TransactionDrawer.vue'
import TransactionFormAccount1 from './components/TransactionFormAccount1.vue'
import TransactionFormAccount2 from './components/TransactionFormAccount2.vue'
import TransactionFormAccount3 from './components/TransactionFormAccount3.vue'
import TransactionFormAccount4 from './components/TransactionFormAccount4.vue'
import TransactionFormAmount1 from './components/TransactionFormAmount1.vue'
import TransactionFormAmount2 from './components/TransactionFormAmount2.vue'
import TransactionFormAmount3 from './components/TransactionFormAmount3.vue'
import TransactionFormAmount4 from './components/TransactionFormAmount4.vue'
import TransactionFormAsset1 from './components/TransactionFormAsset1.vue'
import TransactionFormAsset2 from './components/TransactionFormAsset2.vue'
import TransactionFormAsset3 from './components/TransactionFormAsset3.vue'
import TransactionFormAsset4 from './components/TransactionFormAsset4.vue'
import TransactionFormDate from './components/TransactionFormDate.vue'
import TransactionFormDescription from './components/TransactionFormDescription.vue'
import TransactionFormType from './components/TransactionFormType.vue'

export class TransactionClass extends Entity<Transaction> {
  private assetsBunchStore
  private bunchStore
  private transactionsStore
  private transactionsSettingsStore
  private modalsStore
  private repositoriesStore

  private strictValidation = true

  constructor(data: Partial<Transaction>, created = false, strictValidation = true) {
    if (data.description === null) {
      data.description = ''
    }
    super(data, TRANSACTION_DEFAULT_DATA, created)
    this.assetsBunchStore = useAssetsBunchStore()
    this.bunchStore = useTransactionsBunchStore()
    this.modalsStore = useModalsStore()
    this.repositoriesStore = useRepositoriesStore()
    this.transactionsSettingsStore = useTransactionsSettingsStore()
    this.transactionsStore = useTransactionsStore()
    this.cachedData = { settings: undefined }
    this.strictValidation = strictValidation
  }

  prepareFields(path: keyof Transaction, oldValue: any, newValue: any) {
    if (path !== TRANSACTION_FIELD.TYPE || newValue === oldValue) return
    const asset =
          this.assetsBunchStore.getElementByName(this.repositoriesStore.getCurrentCurrency) ||
          this.assetsBunchStore.getElementByTicker(this.repositoriesStore.getCurrentCurrency)
    const currencyID = asset?.id
    const settings = this.transactionsSettingsStore?.getList.find(
      item => item.name === newValue,
    )?.entries
    if (!settings) return
    remove(this.data.entries, (_, index) => index >= settings.length)

    for (let i = 0; i < settings.length; i++) {
      const account_id = preSetAsset(
        this.data.entries[i]?.account_id,
        settings[i].account,
        this.assetsBunchStore,
        currencyID,
      )
      const amount = preSetAmount(
        this.data.entries[i]?.amount,
        settings[i].amount,
      )
      const asset_id = preSetAsset(
        this.data.entries[i]?.asset_id,
        settings[i].asset,
        this.assetsBunchStore,
        currencyID,
      )
      set(this.data, ['entries', i], { account_id, amount, asset_id })
    }
  }

  get settings(): TransactionSettings | undefined {
    this.prepareSettings()
    return this.cachedData?.settings
  }

  get emptyAccountId1() {
    return !this.settings?.entries[0]?.account
  }
  get emptyAccountId2() {
    return !this.settings?.entries[1]?.account
  }
  get emptyAccountId3() {
    return !this.settings?.entries[2]?.account
  }
  get emptyAccountId4() {
    return !this.settings?.entries[3]?.account
  }

  get emptyAmount1() {
    return !this.settings?.entries[0]?.amount
  }
  get emptyAmount2() {
    return !this.settings?.entries[1]?.amount
  }
  get emptyAmount3() {
    return !this.settings?.entries[2]?.amount
  }
  get emptyAmount4() {
    return !this.settings?.entries[3]?.amount
  }

  get emptyAssetId1() {
    return !this.settings?.entries[0]?.asset
  }
  get emptyAssetId2() {
    return !this.settings?.entries[1]?.asset
  }
  get emptyAssetId3() {
    return !this.settings?.entries[2]?.asset
  }
  get emptyAssetId4() {
    return !this.settings?.entries[3]?.asset
  }

  get entriesLength() {
    return this.settings?.entries.length || 0
  }

  private entry(index: number) {
    return computed({
      get: () => {
        return this.field<TransactionEntry[]>(TRANSACTION_FIELD.ENTRIES).value[index]
      },
      set: value => {
        this.field<TransactionEntry[]>(TRANSACTION_FIELD.ENTRIES).value[index] = value
        this.validateData()
      }
    })
  }

  entry1() {
    return this.entry(0)
  }

  entry2() {
    return this.entry(1)
  }

  entry3() {
    return this.entry(2)
  }

  entry4() {
    return this.entry(3)
  }

  get displayDate() {
    return stringToLocalDateString(
      this.field<string>(TRANSACTION_FIELD.DATE).value,
    )
  }

  get displayAccountId1() {
    if (this.emptyAccountId1) return ' '
    return displayAsset(
      this.entry1().value?.account_id,
      this.assetsBunchStore.getList,
    )
  }
  get displayAccountId2() {
    if (this.emptyAccountId2) return ' '
    return displayAsset(
      this.entry2().value?.account_id,
      this.assetsBunchStore.getList,
    )
  }
  get displayAccountId3() {
    if (this.emptyAccountId3) return ' '
    return displayAsset(
      this.entry3().value?.account_id,
      this.assetsBunchStore.getList,
    )
  }
  get displayAccountId4() {
    if (this.emptyAccountId4) return ' '
    return displayAsset(
      this.entry4().value?.account_id,
      this.assetsBunchStore.getList,
    )
  }

  get displayAmount1() {
    if (this.emptyAmount1) return ' '
    return displayAmount(
      this.entry1().value?.amount,
      this.settings?.entries[0]?.amount?.rule === TransactionAmountRule.PERCENT,
    )
  }
  get displayAmount2() {
    if (this.emptyAmount2) return ' '
    return displayAmount(
      this.entry2().value?.amount,
      this.settings?.entries[1]?.amount?.rule === TransactionAmountRule.PERCENT,
    )
  }
  get displayAmount3() {
    if (this.emptyAmount3) return ' '
    return displayAmount(
      this.entry3().value?.amount,
      this.settings?.entries[2]?.amount?.rule === TransactionAmountRule.PERCENT,
    )
  }
  get displayAmount4() {
    if (this.emptyAmount4) return ' '
    return displayAmount(
      this.entry4().value?.amount,
      this.settings?.entries[3]?.amount?.rule === TransactionAmountRule.PERCENT,
    )
  }

  get displayAssetId1() {
    if (this.emptyAssetId1) return ' '
    return displayAsset(
      this.entry1().value?.asset_id,
      this.assetsBunchStore.getList,
    )
  }
  get displayAssetId2() {
    if (this.emptyAssetId2) return ' '
    return displayAsset(
      this.entry2().value?.asset_id,
      this.assetsBunchStore.getList,
    )
  }
  get displayAssetId3() {
    if (this.emptyAssetId3) return ' '
    return displayAsset(
      this.entry3().value?.asset_id,
      this.assetsBunchStore.getList,
    )
  }
  get displayAssetId4() {
    if (this.emptyAssetId4) return ' '
    return displayAsset(
      this.entry4().value?.asset_id,
      this.assetsBunchStore.getList,
    )
  }

  get isReadonly() {
    return !!this.settings?.readonly
  }

  getEntryAsset(index: number, value: string) {
    const asset = this.assetsBunchStore.getElementByName(value)
    if (!asset) return
    const entry = (this[`entry${index + 1}` as keyof TransactionClass] as TransactionComputedEntry).call(this)
    if (!entry.value) {
      entry.value = {
        account_id: '',
        amount: null,
        asset_id: '',
      }
    }
    return asset
  }

  pasteType(value: string) {
    return new Promise((resolve, reject) => {
      if (
        !this.transactionsSettingsStore.getList.find(item => item.name === value)
      ) {
        reject('Invalid type')
        return
      }
      this.field(TRANSACTION_FIELD.TYPE).value = value
      super.paste()
      resolve(true)
    })
  }

  pasteDate(value: string) {
    return new Promise((resolve, reject) => {
      const { isoDate, isAmbiguous } = parseDate(value)

      if (isAmbiguous) {
        reject(`The pasted date "${value}" is ambiguous and could not be processed.`)
        return
      }

      if (!isoDate) {
        reject('Invalid date')
        return
      }

      this.field(TRANSACTION_FIELD.DATE).value = isoDate
      resolve(true)
    })
  }

  pasteAccountId(index: number, value: string, check: boolean, isPowerMode?: boolean) {

    return new Promise((resolve, reject) => {
      const key = `entry${index + 1}` as keyof TransactionClass
      const entry = (this[key] as TransactionComputedEntry).call(this)
      if (value.trim() === '') {
        entry.value = {
          ...entry.value,
          account_id: ''
        }
        resolve(true)
        return
      }
      const emptyKey = `emptyAccount${index + 1}` as keyof TransactionClass
      if (this[emptyKey]) {
        reject('Empty account')
        return
      }
      if (check) {
        const asset = this.getEntryAsset(index, value)?.get()
        if (!asset) {
          reject('Asset not found')
          return
        }
        if (!isPowerMode && this.settings?.excludeLinkedAccounts && !!asset?.linked_account_id) {
          reject('Linked account is not allowed')
          return
        }
        if (
          !this.settings?.entries[index].account?.includes.includes(asset.type)
        ) {
          reject('Asset type is not allowed')
          return
        }
        entry.value = {
          ...entry.value,
          account_id: asset.id
        }
      } else {
        entry.value = {
          ...entry.value,
          account_id: value
        }
      }
      super.paste()
      resolve(true)
    })
  }

  pasteAccountId1(value: string, options: { check?: boolean, isPowerMode?: boolean }) {
    return this.pasteAccountId(0, value, options.check ?? true, options.isPowerMode)
  }

  pasteAccountId2(value: string, options: { check?: boolean, isPowerMode?: boolean }) {
    return this.pasteAccountId(1, value, options.check ?? true, options.isPowerMode)
  }

  pasteAccountId3(value: string, options: { check?: boolean, isPowerMode?: boolean }) {
    return this.pasteAccountId(2, value, options.check ?? true, options.isPowerMode)
  }

  pasteAmount(index: number, value: string) {
    return new Promise((resolve, reject) => {
      const key = `entry${index + 1}` as keyof TransactionClass
      const entry = (this[key] as TransactionComputedEntry).call(this)
      if (value.trim() === '') {
        entry.value = {
          ...entry.value,
          amount: null
        }
        resolve(true)
        return
      }
      const emptyKey = `emptyAmount${index + 1}` as keyof TransactionClass
      if (this[emptyKey]) {
        reject('Empty amount')
        return
      }
      const rule = this.settings?.entries[index].amount?.rule
      let amount = numberParse(value)
      if (isNaN(amount)) {
        reject('Invalid amount')
        return
      }
      if (rule === TransactionAmountRule.PERCENT && value.includes('%')) amount = amount / 100
      if (rule && NEGATIVE_RULES.includes(rule)) {
        amount = -Math.abs(amount)
      }
      if (rule && POSITIVE_RULES.includes(rule)) {
        amount = Math.abs(amount)
      }
      entry.value = {
        ...entry.value,
        amount
      }
      super.paste()
      resolve(true)
    })
  }

  pasteAmount1(value: string) {
    return this.pasteAmount(0, value)
  }

  pasteAmount2(value: string) {
    return this.pasteAmount(1, value)
  }

  pasteAmount3(value: string) {
    return this.pasteAmount(2, value)
  }

  pasteAssetId(index: number, value: string, check: boolean) {
    return new Promise((resolve, reject) => {
      const key = `entry${index + 1}` as keyof TransactionClass
      const entry = (this[key] as TransactionComputedEntry).call(this)
      if (value.trim() === '') {
        entry.value = {
          ...entry.value,
          asset_id: ''
        }
        resolve(true)
        return
      }
      const emptyKey = `emptyAsset${index + 1}` as keyof TransactionClass
      if (this[emptyKey]) {
        reject('Empty asset')
        return
      }
      if (check) {
        const asset = this.getEntryAsset(index, value)
        if (!asset) {
          reject('Asset not found')
          return
        }
        const assetType = asset.get().type
        if (!this.settings?.entries[index].asset?.includes.includes(assetType)) {
          reject('Asset type is not allowed')
          return
        }
        entry.value = {
          ...entry.value,
          asset_id: asset.id
        }
      } else {
        entry.value = {
          ...entry.value,
          asset_id: value
        }
      }
      super.paste()
      resolve(true)
    })
  }

  pasteAssetId1(value: string, options: { check?: boolean }) {
    return this.pasteAssetId(0, value, options.check ?? true)
  }

  pasteAssetId2(value: string, options: { check?: boolean }) {
    return this.pasteAssetId(1, value, options.check ?? true)
  }

  pasteAssetId3(value: string, options: { check?: boolean }) {
    return this.pasteAssetId(2, value, options.check ?? true)
  }

  get copyAmount1() {
    return this.entry1().value?.amount
  }

  get copyAmount2() {
    return this.entry2().value?.amount
  }

  get copyAmount3() {
    return this.entry3().value?.amount
  }

  get getModal() {
    return markRaw(TransactionDrawer)
  }

  getFormType() {
    return TransactionFormType
  }

  getFormDate() {
    return TransactionFormDate
  }

  getFormAccountId1() {
    if (this?.emptyAccountId1) return
    return TransactionFormAccount1
  }
  getFormAccountId2() {
    if (this?.emptyAccountId2) return
    return TransactionFormAccount2
  }
  getFormAccountId3() {
    if (this?.emptyAccountId3) return
    return TransactionFormAccount3
  }
  getFormAccountId4() {
    if (this?.emptyAccountId4) return
    return TransactionFormAccount4
  }

  getFormAmount1() {
    if (this.emptyAmount1) return
    return TransactionFormAmount1
  }
  getFormAmount2() {
    if (this.emptyAmount2) return
    return TransactionFormAmount2
  }
  getFormAmount3() {
    if (this.emptyAmount3) return
    return TransactionFormAmount3
  }
  getFormAmount4() {
    if (this.emptyAmount4) return
    return TransactionFormAmount4
  }

  getFormAssetId1() {
    if (this.emptyAssetId1) return
    return TransactionFormAsset1
  }
  getFormAssetId2() {
    if (this.emptyAssetId2) return
    return TransactionFormAsset2
  }
  getFormAssetId3() {
    if (this.emptyAssetId3) return
    return TransactionFormAsset3
  }
  getFormAssetId4() {
    if (this.emptyAssetId4) return
    return TransactionFormAsset4
  }

  getFormDescription() {
    return TransactionFormDescription
  }

  private validateAsset(settings: TransactionSettingsAsset) {
    if (!this.strictValidation) {
      return settings.optional ? undefined : rules.required.nullable()
    }
    let result = rules.required
    if (settings.includes) {
      result = dependencyRules.oneOf(settings.includes, !!settings.optional)
    }
    if (settings.value) {
      result = dependencyRules.equal(settings.value, !!settings.optional)
    }
    return result.nullable()
  }

  private validateEntries() {
    const settings = this.settings?.entries
    return (_: TransactionEntry, options: any) => {
      const entrySettings = settings?.[options.index]
      const result: Record<string, Rule> = {}
      if (entrySettings?.account) {
        result.account_id = this.validateAsset(entrySettings.account)
      }
      if (entrySettings?.amount) {
        result.amount = makeRequired(rules[entrySettings.amount.rule])
      }
      if (entrySettings?.asset) {
        result.asset_id = this.validateAsset(entrySettings.asset)
      }
      return yup.object(result)
    }
  }

  validate() {
    const entriesRules = this.validateEntries()
    const result: Record<string, Rule> = {
      type: rules.required,
      date: makeRequired(rules.date),
      entries: yup.array().of(yup.lazy(entriesRules) as any),
      description: rules.limited,
    }
    return result
  }

  validateData() {
    const data = cloneDeep(this.data)
    data.entries = data.entries.map((entry, index) => {
      const settings = this.settings?.entries[index]
      if (entry.account_id) {
        entry.account_id = settings?.account?.value
          ? this.assetsBunchStore.getNameById(entry.account_id)
          : this.assetsBunchStore.getTypeById(entry.account_id)
      }
      if (entry.asset_id) {
        entry.asset_id = settings?.asset?.value
          ? this.assetsBunchStore.getNameById(entry.asset_id)
          : this.assetsBunchStore.getTypeById(entry.asset_id)
      }
      return entry
    })
    super.validateData(data)
  }

  handleErrors(errors: EntityErrors) {
    return Object.fromEntries(
      Object.entries(errors).map(([key, value]) => {
        if (key.includes('entries[')) {
          const matches = key.match(/(\d+)\]\.(.+)/)
          if (matches?.[1] && matches[2]) {
            const index = +matches[1]
            const entryTitle = this.settings?.entries[index]?.title
            const field = matches[2].replace('_id', '')
            const newKey = `"${entryTitle}" ${field}`
            return [newKey, value]
          }
        }
        return [key, value]
      }),
    )
  }

  private removeFromBunch() {
    this.bunchStore.getList.delete(this.id)
  }

  private removeFromForms() {
    this.modalsStore.getList.delete(this.id)
  }

  remove() {
    this.removeFromBunch()
    this.removeFromForms()
  }

  private prepareSettings() {
    if (!this.transactionsSettingsStore?.getList.length) return
    const type = this.field<string>(TRANSACTION_FIELD.TYPE).value
    if (this.cachedData.settings?.name !== type) {
      const settings = this.transactionsSettingsStore?.getList.find(
        item => item.name === type,
      )
      this.cachedData.settings = settings
    }
  }

  resetSettings() {
    this.cachedData.settings = undefined
    this.prepareSettings()
  }

  async store() {
    try {
      const data = this.get()
      const result = await this.transactionsStore.store(data)
      this.remove()
      if (result !== undefined) {
        this.reset(result)
        this.bunchStore.unshiftElement(this)
        this.callEvent(EntityEvent.STORED)
        this.strictValidation = true
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async update() {
    try {
      const result = await this.transactionsStore.update(this.get())
      if (result === undefined) {
        this.cancel()
      } else {
        this.callEvent(EntityEvent.UPDATED)
        this.reset(result)
      }
    } catch (e) {
      throw Error(e as string)
    }
  }

  async destroy() {
    try {
      if (!this.isNew) {
        await this.transactionsStore.destroy(this.id)
        this.callEvent(EntityEvent.DESTROYED)
      }
      this.remove()
    } catch (e) {
      throw Error(e as string)
    }
  }
}
